import TicketCuticle from '@/components/manage/TicketCuticle';
import PriceDisplay from '@/components/PriceDisplay';
import { TransText } from '@/i18n/trans/text';
import type { GetVouchersResponseDTO } from '@/types/dto/agent';
import { cn } from '@/utils/cn';
import { format } from '@/utils/dateTime';
import { Badge, Icons, Typography } from '@fleet/webshop-ui';
import type { FC } from 'react';

type VoucherItem = NonNullable<GetVouchersResponseDTO['items']>[number];

interface VoucherSummaryProps {
  voucher: VoucherItem;
}

const VoucherSummary: FC<VoucherSummaryProps> = ({ voucher }) => {
  const validUntilDate =
    voucher.validUntil && format(voucher.validUntil, 'EEE, dd MMM yyyy');

  return (
    <div className={cn('flex h-[150px] w-full flex-row rounded-lg bg-white')}>
      <div
        className={cn(
          'flex flex-1 flex-col gap-3 rounded-l-lg rounded-tr-none border border-r-0 border-neutral-light',
          'pb-4 pl-4 pr-2 pt-3'
        )}
      >
        {voucher.type?.name && (
          <div className="inline-flex w-fit items-center gap-2 rounded-[2px] bg-primary-pale p-1">
            <Typography variant="label" className="text-center text-primary">
              {voucher.type.name}
            </Typography>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <div className="flex items-baseline gap-1">
            <Typography
              variant="heading2"
              className="shrink-0 text-right text-primary"
            >
              <PriceDisplay amount={voucher.balance} />
            </Typography>

            <div className="flex flex-1 items-center gap-0.5 text-xs leading-none tracking-tight text-neutral">
              <span className="leading-4">
                <Typography variant="body2">/</Typography>
              </span>
              <span className="leading-4">
                <Typography variant="body2">
                  <PriceDisplay amount={voucher.nominalValue} />
                </Typography>
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <Typography variant="body2" className="text-neutral">
              {voucher.code}
            </Typography>
            <div className="size-4" />
          </div>
        </div>
      </div>

      <div className="h-full">
        <TicketCuticle isVertical={true} />
      </div>

      <div
        className={cn(
          'flex w-[120px] flex-col justify-between gap-3 rounded-r-lg px-2 py-4 pr-4',
          'border border-l-0 border-neutral-light'
        )}
      >
        {voucher.isCancelled && (
          <Badge className="w-fit gap-1" variant="disabled">
            <Icons.cancel width={16} height={16} />
            <Typography variant="label">
              <TransText i18nKey="cancelled" />
            </Typography>
          </Badge>
        )}
        <div className="flex flex-1 flex-col justify-end gap-1">
          <div className="flex flex-col items-start justify-center gap-1">
            <Typography variant="body2" className="text-neutral">
              <TransText i18nKey="validUntil" />:
            </Typography>
          </div>
          <div className="flex flex-col items-start justify-center gap-1">
            <div className="flex items-baseline text-right text-xs leading-4 text-dark">
              {validUntilDate ? (
                <>
                  <span className="font-bold tracking-tight">
                    {format(voucher.validUntil!, 'dd MMM')}
                  </span>
                  <span className="font-medium tracking-tight">
                    , {format(voucher.validUntil!, 'yyyy')}
                  </span>
                </>
              ) : (
                '-'
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoucherSummary;
